import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { awardsVerdictV13 as copy } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

const VerdictMedia = ({ media }) => {
  const cls = "absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]";
  if (media.type === "video") {
    return (
      <video
        data-testid="verdict-media-video"
        poster={media.poster}
        aria-label={media.alt}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className={cls}
        style={{ objectPosition: media.position }}
      >
        <source src={media.mp4} type="video/mp4" />
        <source src={media.webm} type="video/webm" />
      </video>
    );
  }
  return (
    <img
      src={media.url}
      alt={media.alt}
      className={cls}
      style={{ objectPosition: media.position }}
      draggable={false}
    />
  );
};

// V1.3 Refinement 12 — "The jury's verdict" (V1.3 ONLY).
// Three evidence cards: feature media + the juror quote that praises it + award chip. No carousel.
export const AwardsSectionV13 = () => (
  <section
    id="awards"
    data-testid="awards-section"
    className="bg-[var(--strollee-bg)] px-6 py-20 lg:px-10 lg:py-24"
  >
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-120px" }}
      transition={{ duration: 0.8, ease: EASE }}
      className="flex items-end justify-between gap-12"
    >
      <div className="max-w-[720px]">
        <p
          data-testid="awards-eyebrow"
          className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
        >
          {copy.eyebrow}
        </p>
        <h2 className="mt-4 text-[40px] font-semibold leading-[1.06] tracking-[-0.03em] text-[var(--strollee-ink)] lg:text-[52px]">
          {copy.headlineLines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </h2>
      </div>
      <div className="flex flex-col items-end gap-5">
        <div className="flex items-center gap-6" data-testid="awards-badges">
          {copy.badges.map((b) => (
            <div key={b.id} data-testid={`awards-badge-${b.id}`} className="flex items-center gap-3">
              <img src={b.url} alt={b.alt} className="h-[56px] w-auto" draggable={false} />
              <div className="leading-tight">
                <p className="text-[13px] font-semibold text-[var(--strollee-ink)]">{b.label}</p>
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--strollee-muted)]">{b.meta}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="max-w-[420px] text-right text-base leading-relaxed text-[var(--strollee-muted)]">
          {copy.support}
        </p>
      </div>
    </motion.div>

    {/* TRIPTYCH */}
    <div className="mt-12 grid grid-cols-3 gap-4 lg:mt-14 lg:gap-5" data-testid="awards-verdicts">
      {copy.verdicts.map((v, i) => (
        <motion.article
          key={v.id}
          data-testid={`verdict-${v.id}`}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: i * 0.12 }}
          className="group relative aspect-[4/5] overflow-hidden rounded-[var(--strollee-radius-media)] bg-[var(--strollee-dark)]"
        >
          <VerdictMedia media={v.media} />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A15]/90 via-[#0F1A15]/40 via-45% to-transparent" />

          <span
            data-testid={`verdict-award-${v.id}`}
            className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-[#0F1A15]/85 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-md"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#8FB89E]" />
            {v.award}
          </span>

          <blockquote className="absolute inset-x-0 bottom-0 p-6 text-white transition-transform duration-500 ease-out group-hover:-translate-y-1 lg:p-7">
            <p
              data-testid={`verdict-quote-${v.id}`}
              className="text-[19px] font-medium leading-[1.3] tracking-[-0.015em] lg:text-[21px]"
            >
              “{v.quoteBefore}
              <span className="text-[#8FB89E]">{v.highlight}</span>
              {v.quoteAfter}”
            </p>
            <footer className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
              {copy.attribution} · {v.award}
            </footer>
          </blockquote>
        </motion.article>
      ))}
    </div>

    {/* FOOTER ROW */}
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
      className="mt-10 flex items-center justify-between gap-8 border-t border-[var(--strollee-line)] pt-7 lg:mt-12"
    >
      <div
        data-testid="awards-trust-line"
        className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--strollee-muted)]"
      >
        {copy.footerStats.map((s, i) => (
          <span key={s} className="flex items-center gap-5">
            {i > 0 && <span className="h-1 w-1 rounded-full bg-[var(--strollee-accent)]" />}
            {s}
          </span>
        ))}
      </div>
      <a
        href="#safety"
        data-testid="awards-cta"
        className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] transition-colors duration-200 hover:text-[var(--strollee-accent)]"
      >
        {copy.cta}
        <ArrowUpRight
          size={16}
          strokeWidth={2.2}
          className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
        />
      </a>
    </motion.div>
  </section>
);
